'use client';

import { useEffect, useRef, useState } from 'react';
import { getLang, useTranslation } from '@root/src/i18n/client';
import Link from 'next/link';
import styles from '../header.module.css';
import {
  notifyHeaderDropdownOpen,
  subscribeToHeaderDropdownOpen,
} from '../HeaderDesktop/components/dropdownSync';

const dropdownSummaryClass =
  "relative flex cursor-pointer items-center gap-1 px-2 py-1 text-gray-600 transition-colors duration-300 after:absolute after:bottom-0 after:left-2 after:h-[2px] after:w-[calc(100%-1rem)] after:origin-left after:scale-x-0 after:rounded-full after:bg-[#667348] after:transition-transform after:duration-300 hover:text-[#667348] hover:after:scale-x-100 group-open:text-[#667348] group-open:after:scale-x-100";

const defaultCategoryItems = [
  { name: 'Trà xanh', href: '/tra-xanh' },
  { name: 'Trà ô long', href: '/tra-o-long' },
  { name: 'Trà đen', href: '/tra-den' },
  { name: 'Trà thảo mộc', href: '/tra-thao-moc' },
];

interface CategoryItem {
  id?: string | number;
  slug?: string;
  parentId?: string | number | null;
  name?: string;
  nameEn?: string;
  nameZh?: string;
  order?: string | number | null;
}

interface CategoryDropdownProps {
  categories?: CategoryItem[];
}

interface CategoryGroup {
  id: string;
  parentName: string;
  parent: CategoryItem;
  parentHref: string;
  children: Array<{
    id: string;
    name: string;
    href: string;
  }>;
}

function pickLocalizedName(item: CategoryItem, lang: string) {
  if (lang === 'en') {
    return item.nameEn || item.name || item.slug || '';
  }
  if (lang === 'zh') {
    return item.nameZh || item.name || item.slug || '';
  }
  return item.name || item.slug || '';
}

function buildCategoryPath(
  category: CategoryItem,
  categoryMap: Map<string, CategoryItem>
) {
  const segments: string[] = [];
  let current: CategoryItem | undefined = category;

  while (current?.slug) {
    segments.unshift(current.slug);
    if (!current.parentId) break;
    current = categoryMap.get(String(current.parentId));
  }

  return `/${segments.join('/')}`;
}

function isRootCategory(item: CategoryItem) {
  return item.parentId === null || item.parentId === undefined || item.parentId === '';
}

function parseOrder(order?: string | number | null) {
  if (typeof order === 'number') {
    return [order];
  }

  if (typeof order === 'string' && order.trim()) {
    return order
      .split('-')
      .map(part => Number.parseInt(part, 10))
      .filter(part => Number.isFinite(part));
  }

  return [];
}

function compareCategoryOrder(a: CategoryItem, b: CategoryItem) {
  const orderA = parseOrder(a.order);
  const orderB = parseOrder(b.order);
  const maxLength = Math.max(orderA.length, orderB.length);

  for (let index = 0; index < maxLength; index += 1) {
    const partA = orderA[index];
    const partB = orderB[index];

    if (partA === undefined && partB === undefined) break;
    if (partA === undefined) return 1;
    if (partB === undefined) return -1;
    if (partA !== partB) return partA - partB;
  }

  return (a.name || a.slug || '').localeCompare(b.name || b.slug || '', 'vi');
}

export default function CategoryDropdown({ categories = [] }: CategoryDropdownProps) {
  const { t } = useTranslation('common');
  const lang = getLang();
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const [activeParentId, setActiveParentId] = useState<string | null>(null);
  const categoryMap = new Map<string, CategoryItem>(
    categories.map(item => [String(item.id || item.slug), item])
  );

  const parentCategories = categories
    .filter(item => item?.slug && isRootCategory(item))
    .sort(compareCategoryOrder);

  const categoryGroups: CategoryGroup[] = parentCategories.map(parent => {
    const children = categories
      .filter(item => item?.slug && String(item.parentId) === String(parent.id))
      .sort(compareCategoryOrder)
      .map(item => ({
        id: String(item.id || item.slug),
        name: pickLocalizedName(item, lang),
        href: buildCategoryPath(item, categoryMap),
      }));

    return {
      id: String(parent.id || parent.slug),
      parentName: pickLocalizedName(parent, lang),
      parent,
      parentHref: buildCategoryPath(parent, categoryMap),
      children,
    };
  });

  useEffect(() => {
    return subscribeToHeaderDropdownOpen('category', () => {
      if (detailsRef.current?.open) {
        detailsRef.current.open = false;
      }
    });
  }, []);

  const handleToggle = () => {
    if (detailsRef.current?.open) {
      notifyHeaderDropdownOpen('category');
      return;
    }

    setActiveParentId(null);
  };

  const handleCloseDropdown = () => {
    if (detailsRef.current?.open) {
      detailsRef.current.open = false;
    }

    setActiveParentId(null);
  };

  useEffect(() => {
    const closeDropdown = () => {
      if (detailsRef.current?.open) {
        detailsRef.current.open = false;
      }

      setActiveParentId(null);
    };

    const handlePointerOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target;

      if (target instanceof Node && !detailsRef.current?.contains(target)) {
        closeDropdown();
      }
    };

    document.addEventListener('mousedown', handlePointerOutside);
    document.addEventListener('touchstart', handlePointerOutside);
    window.addEventListener('scroll', closeDropdown, true);

    return () => {
      document.removeEventListener('mousedown', handlePointerOutside);
      document.removeEventListener('touchstart', handlePointerOutside);
      window.removeEventListener('scroll', closeDropdown, true);
    };
  }, []);

  return (
    <details
      ref={detailsRef}
      className={`group relative ${styles.collectionDropdown}`}
      onToggle={handleToggle}
    >
      <summary className={dropdownSummaryClass}>
        {t('lytra.headerCategories')}
        <span className="text-[10px]">▾</span>
      </summary>

      <div className={`${styles.collectionPanel} absolute left-0 top-full z-50 mt-1 min-w-[260px] overflow-hidden rounded-[24px] border border-[#EDE6D8] bg-white p-3 shadow-[0_18px_45px_rgba(34,58,39,0.08)]`}>
        {categoryGroups.length > 0 ? (
          <div className="max-h-[70vh] overflow-y-auto">
            {categoryGroups.map(group => (
              <div
                key={group.id}
                className="rounded-2xl"
                onMouseEnter={() => setActiveParentId(group.children.length > 0 ? group.id : null)}
                onMouseLeave={() => setActiveParentId(current => (current === group.id ? null : current))}
              >
                <div className="flex items-center justify-between gap-3 rounded-2xl transition-colors hover:bg-[#F8F2E7]">
                  <Link
                    href={group.parentHref}
                    className="block flex-1 rounded-2xl px-4 py-2 text-sm text-[#223A27] transition-colors hover:bg-[#F4E8D8]"
                    onFocus={() => setActiveParentId(group.children.length > 0 ? group.id : null)}
                    onClick={handleCloseDropdown}
                  >
                    {group.parentName}
                  </Link>

                  {group.children.length > 0 && (
                    <span className="pr-4 text-[10px] text-[#8B7B59]">▸</span>
                  )}
                </div>

                {activeParentId === group.id && group.children.length > 0 && (
                  <div className="ml-4 border-l border-[#EDE6D8] pl-3 pb-2">
                    {group.children.map(child => (
                      <Link
                        key={child.id}
                        href={child.href}
                        className="mt-1 block rounded-2xl px-4 py-2 text-sm text-[#4B5A42] transition-colors hover:bg-[#F8F2E7] hover:text-[#223A27]"
                        onClick={handleCloseDropdown}
                      >
                        {child.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          defaultCategoryItems.map(item => (
            <Link
              key={item.href}
              href={item.href}
              className="block rounded-2xl px-4 py-2 text-sm text-[#223A27] transition-colors hover:bg-[#F4E8D8]"
              onClick={handleCloseDropdown}
            >
              {item.name}
            </Link>
          ))
        )}
      </div>
    </details>
  );
}
